import { Bot, RefreshCw, Sparkles } from "lucide-react";
import type { AiExplanation } from "@/stores/ai-explain";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardSub, CardTitle } from "@/components/ui/card";

export function DashboardAiSummaryPanel({
  showResummarize,
  summarizeReady,
  summarizing,
  gatheringSummaryData,
  computingSummary,
  summaryError,
  summaryData,
  onSummarize,
}: {
  showResummarize: boolean;
  summarizeReady: boolean;
  summarizing: boolean;
  gatheringSummaryData: boolean;
  computingSummary: boolean;
  summaryError: string | null;
  summaryData: AiExplanation | null;
  onSummarize: (force: boolean) => void;
}) {
  return (
    <Card>
      <CardContent className="flex h-full flex-col gap-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-start gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-aurora-soft text-violet">
              <Bot size={18} className="mt-0.5 text-violet" />
            </div>
            <div>
              <CardTitle>AI Summary</CardTitle>
              <CardSub>
                Actionable recommendations with device-level fixes based on the
                current dashboard state.
              </CardSub>
            </div>
          </div>
          {showResummarize ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onSummarize(true)}
              disabled={!summarizeReady || summarizing}
            >
              <Sparkles size={12} className="text-violet" />
              Re-summarize
            </Button>
          ) : null}
        </div>
        <div className="flex min-h-[160px] flex-1">
          {gatheringSummaryData ? (
            <div className="flex h-full w-full flex-col items-center justify-center gap-2 rounded-lg border border-line/70 bg-surface-tint/20 px-4 py-4 text-center">
              <Button size="sm" variant="ghost" disabled>
                <RefreshCw size={18} className="text-violet animate-spin" />
                Gathering data
              </Button>
              <div className="text-sm text-fg-mute">
                Opus is collecting data for a better analysis
              </div>
            </div>
          ) : computingSummary ? (
            <div className="flex h-full w-full flex-col items-center justify-center gap-2 rounded-lg border border-line/70 bg-surface-tint/20 px-4 py-4 text-center">
              <Button size="sm" variant="ghost" disabled>
                <RefreshCw size={18} className="text-violet animate-spin" />
                Computing
              </Button>
              <div className="text-sm text-fg-mute">
                Building a recommendation summary from collected telemetry
              </div>
            </div>
          ) : summaryError ? (
            <div className="flex h-full w-full flex-col items-center justify-center gap-3 rounded-lg border border-peach/30 bg-peach/10 px-4 py-4 text-center">
              <div className="text-sm text-peach">{summaryError}</div>
              <Button
                size="sm"
                onClick={() => onSummarize(true)}
                disabled={!summarizeReady || summarizing}
              >
                Try Summary Again
              </Button>
            </div>
          ) : summaryData ? (
            <div className="flex h-full w-full flex-col rounded-lg border border-line bg-surface-tint/30 px-3 py-3">
              <div className="text-sm font-semibold text-fg">
                {summaryData.summary}
              </div>
              <div className="mt-1 flex-1 text-sm whitespace-pre-wrap text-fg-dim">
                {summaryData.details}
              </div>
              <div className="mt-2 font-mono text-[10.5px] text-fg-mute">
                {summaryData.model} · {summaryData.elapsedMs} ms
              </div>
            </div>
          ) : (
            <div className="flex h-full w-full items-center rounded-lg border border-line/70 bg-surface-tint/20 px-4 py-4 text-sm text-fg-mute">
              Summary will generate automatically when dashboard data is ready.
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
